import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Wand2, Copy, Check, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OCCASION_OPTIONS = ['Birthday', 'Wedding', 'Anniversary', 'Graduation', 'New Job', 'Thank You', 'Just Because'];
const TONE_OPTIONS = ['Heartfelt', 'Playful', 'Romantic', 'Funny', 'Inspiring'];

export const MessageWriter = () => {
  const [recipient, setRecipient] = useState('');
  const [occasion, setOccasion] = useState('Birthday');
  const [relationship, setRelationship] = useState('');
  const [tone, setTone] = useState('Heartfelt');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!recipient.trim()) {
      toast.error('Add a name', { description: "Tell us who this gift is for." });
      return;
    }
    setLoading(true);
    setCopied(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/generate-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient, occasion, relationship, tone, notes }),
      });
      const data = await res.json();
      if (res.ok && data.message) {
        setMessage(data.message);
      } else {
        toast.error('Hmm, try again', { description: data.detail || 'Could not write a message right now.' });
      }
    } catch {
      toast.error('Something went wrong', { description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="try-ai" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-2xl text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-primary shadow-sm">
          <Sparkles className="h-3.5 w-3.5" /> Powered by AI
        </div>
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          Stuck on the words? Let us help.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
          Every UNWP gift carries a message. Try our writer — tell us a little, and we'll
          draft something worth revealing.
        </p>
      </div>

      <div className="mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-2">
        {/* Inputs */}
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
          <div className="grid gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold">Who is it for?</label>
              <Input
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="e.g. Sarah"
                className="h-11 rounded-xl border-border"
                data-testid="ai-recipient-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold">Occasion</label>
                <select
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="ai-occasion-select"
                >
                  {OCCASION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="ai-tone-select"
                >
                  {TONE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold">Your relationship <span className="font-normal text-muted-foreground">(optional)</span></label>
              <Input
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                placeholder="e.g. her mum, my best friend"
                className="h-11 rounded-xl border-border"
                data-testid="ai-relationship-input"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold">A memory or detail <span className="font-normal text-muted-foreground">(optional)</span></label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. our trip to Rome, how she always makes me laugh…"
                className="min-h-[84px] rounded-xl border-border"
                data-testid="ai-notes-input"
              />
            </div>

            <Button
              onClick={generate}
              disabled={loading}
              className="h-12 rounded-full bg-primary font-semibold text-primary-foreground shadow-md transition-transform hover:bg-primary/90 active:scale-95"
              data-testid="ai-generate-button"
            >
              {loading ? (
                <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Writing…</>
              ) : (
                <><Wand2 className="mr-2 h-4 w-4" /> {message ? 'Write another' : 'Write my message'}</>
              )}
            </Button>
          </div>
        </div>

        {/* Output */}
        <div className="relative flex flex-col overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-accent/15 via-card to-card p-6 shadow-sm md:p-8">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
            <Sparkles className="h-4 w-4 text-primary" /> Your message
          </div>
          <div className="flex flex-1 items-center justify-center">
            <AnimatePresence mode="wait">
              {message ? (
                <motion.div
                  key="msg"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="w-full"
                >
                  <p className="font-display text-xl leading-relaxed text-foreground md:text-2xl" data-testid="ai-message-output">
                    "{message}"
                  </p>
                  <Button
                    onClick={copy}
                    variant="outline"
                    className="mt-6 rounded-full border-foreground/15 font-semibold"
                    data-testid="ai-copy-button"
                  >
                    {copied ? <><Check className="mr-2 h-4 w-4 text-primary" /> Copied</> : <><Copy className="mr-2 h-4 w-4" /> Copy message</>}
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center text-muted-foreground"
                >
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Wand2 className="h-7 w-7" />
                  </div>
                  <p className="max-w-xs text-sm">Fill in a few details and your personalized reveal message will appear here.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
